// SourcesPage — "what the record reads" (BUILD-BRIEF-R6 §5.1). One page, four zones:
//
//   sources     one row per public source this CRM watches — enabled, detection, what a
//               refresh last did, and what it filed (held ▪ vs waiting ◇)
//   documents   every VINTAGE kept, across every source — a superseded vintage never vanishes
//   refreshes   what every refresh, on any source, did — one report per run, opened on toggle
//   waiting     entries a source named that the register did not resolve on its own
//   gone        names a new vintage stopped printing — observed presence, never a retraction
//               (§4.1: the organisation, its facts and its links are untouched)
//
// THE THREE HONESTIES THIS PAGE OWES (P11/P12, mirrored from MarketPage's own banner):
//   1. A disabled source's status reads "disabled" in fv--nil — not a blank, not an error.
//   2. "Changed at last check" counts a STATE (the latest refresh's own outcome), never a
//      window of ours (§8.3 #7) — 0 is counted and is fv--nil, not "not collected".
//   3. A route this package does not own that has not shipped yet (a 404/501 from
//      /api/sources) leaves the rail cell UNSET via usePoll's stale-and-labelled contract —
//      never a silent zero. See CLAUDE.md P11 and the Rail contract in components/PageKit.tsx.
import { Fragment, useCallback, useMemo, useState } from 'react';
import {
  Att, Fig, Lnk, Mast, PageShell, Rail, RailCell, Ref, Sep, Sheet, Tag, Tbl, Zone, ZoneHead,
  type TagTone,
} from '../components/PageKit.js';
import { usePoll } from '../components/usePoll.js';
import { useToast } from '../components/Toast.js';
import { useLocale, useT } from '../i18n/index.js';
import { href, open, ROUTES, useDeepLink, useRouteParams } from '../routes.js';
import { request } from '../api/client.js';
import {
  listDocuments, listEntries, listRefreshes, listSources, proposeEntry, refreshSource,
  updateSource, type SourceListRow,
} from '../api/sources.js';
import type {
  Detection, RefreshOutcome, SourceDocument, SourceEntry, SourceRefresh,
} from '../../shared/sources.v1.js';
import './SourcesPage.de.js';
import './SourcesPage.fr.js';

const REFRESHES_LIMIT = 50;

/** The signal a source's OWN detection order leads with — what "next check" names (§5.1). */
const DETECTION_LABEL: Record<Detection, string> = {
  last_modified: 'Last-Modified',
  etag: 'ETag',
  content_hash: 'content hash',
  filename_version: 'filename version',
  page_links: 'page links',
  record_version: 'record version',
};

function outcomeTone(outcome: RefreshOutcome | null | undefined): TagTone {
  switch (outcome) {
    case 'changed': return 'ok';
    case 'failed': return 'broken';
    case 'refused': return 'attend';
    default: return 'neutral';
  }
}

/** A ref-shaped string (`ORG-0012`, `PRO-0044`, `PER-0102`) becomes a Lnk into the personal
 *  index — the one place any ref resolves to its record without this page holding an id. */
function RefLnk({ children }: { children: string }) {
  if (/^(ORG|PRO|PER|INT|COM|OPP)-\d+$/.test(children)) {
    return <Lnk href={href(ROUTES.search({ q: children }))}><Ref>{children}</Ref></Lnk>;
  }
  return <Ref>{children}</Ref>;
}

export default function SourcesPage() {
  const t = useT();
  const { fmtDate, fmtDateTime } = useLocale();
  const toast = useToast();
  const params = useRouteParams();

  const fetchSources = useCallback(() => listSources(), []);
  const sources = usePoll(fetchSources, 30_000);

  const fetchRefreshes = useCallback(() => listRefreshes(undefined, REFRESHES_LIMIT), []);
  const refreshes = usePoll(fetchRefreshes, 30_000);

  const fetchDocuments = useCallback(() => listDocuments(), []);
  const documents = usePoll(fetchDocuments, 60_000);

  const fetchWaiting = useCallback(async (): Promise<{ items: SourceEntry[]; total: number }> => {
    const [unresolved, ambiguous, proposed] = await Promise.all([
      listEntries({ resolution: 'unresolved', status: 'current' }),
      listEntries({ resolution: 'ambiguous', status: 'current' }),
      listEntries({ resolution: 'proposed', status: 'current' }),
    ]);
    const items = [...unresolved.items, ...ambiguous.items, ...proposed.items];
    return { items, total: unresolved.total + ambiguous.total + proposed.total };
  }, []);
  const waiting = usePoll(fetchWaiting, 30_000);

  // §4.1: a name that left a directory is a SIGNAL, not a retraction — the organisation, its
  // facts and its links are untouched, and the operator is the one who reads what it means.
  const fetchGone = useCallback(() => listEntries({ status: 'gone' }), []);
  const gone = usePoll(fetchGone, 60_000);

  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const [expandedRefresh, setExpandedRefresh] = useState<number | null>(null);
  const [openDocGroups, setOpenDocGroups] = useState<Set<string>>(new Set());
  const [busyKey, setBusyKey] = useState<string | null>(null);

  // The arrival intent: a `key` scrolls to its row and opens its documents. It never remounts
  // this page (routes.ts law 1) and is cleared once applied (law 2).
  useDeepLink(params, {
    key: (value) => {
      setExpandedKey(value);
      requestAnimationFrame(() => {
        document.getElementById(`source-${value}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    },
  });

  const rows = sources.data?.items ?? [];
  const total = sources.data?.total ?? rows.length;

  // ── the rail's five figures — arithmetic over the SAME set the sources zone lists ──────────
  const enabledCount = rows.filter((r) => r.enabled).length;
  const docsCurrent = rows.reduce((n, r) => n + (r.documents_current ?? 0), 0);
  const docsSuperseded = rows.reduce((n, r) => n + (r.documents_superseded ?? 0), 0);
  const changedAtLastCheck = rows.filter((r) => r.last_refresh_outcome === 'changed').length;
  const entriesCurrent = rows.reduce((n, r) => n + (r.entries_current ?? 0), 0);
  const entriesResolved = rows.reduce((n, r) => n + (r.entries_resolved ?? 0), 0);
  const entriesWaiting = rows.reduce((n, r) => n + (r.entries_waiting ?? 0), 0);
  const seriesPoints = rows.reduce((n, r) => n + (r.series_points ?? 0), 0);
  const seriesCount = rows.reduce((n, r) => n + (r.series_count ?? 0), 0);

  const lastRefreshAt = useMemo(() => {
    const stamps = rows.map((r) => r.last_checked_at).filter((d): d is string => !!d);
    return stamps.length ? stamps.sort().at(-1) ?? null : null;
  }, [rows]);

  const refreshAll = async () => {
    try {
      await request('/jobs/sources.refresh/run', { method: 'POST' });
      toast('ok', t('Refresh all requested.'));
      await sources.refresh();
    } catch (err) {
      toast('err', err instanceof Error ? err.message : String(err));
    }
  };

  const doRefreshOne = async (key: string) => {
    setBusyKey(key);
    try {
      const { refresh } = await refreshSource(key);
      toast('ok', t('{key}: {outcome}', { key, outcome: t(refresh.outcome ?? 'unchanged') }));
      await Promise.all([sources.refresh(), refreshes.refresh()]);
    } catch (err) {
      toast('err', err instanceof Error ? err.message : String(err));
    } finally {
      setBusyKey(null);
    }
  };

  const toggleEnabled = async (row: SourceListRow) => {
    try {
      await updateSource(row.key, { enabled: !row.enabled });
      await sources.refresh();
    } catch (err) {
      toast('err', err instanceof Error ? err.message : String(err));
    }
  };

  const doPropose = async (entry: SourceEntry) => {
    try {
      const { proposal } = await proposeEntry(entry.id);
      toast('ok', t('Filed {ref}.', { ref: proposal.ref }));
      await waiting.refresh();
    } catch (err) {
      toast('err', err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <PageShell>
      <Mast
        kicker={t('Sources')}
        title={t('What the record reads')}
        sub={sources.data
          ? <>
              <Fig>{total}</Fig> {t('sources')}<Sep />
              <Fig>{docsCurrent}</Fig> {t('documents kept')}
              {lastRefreshAt && <><Sep />{t('last refresh')} <Fig>{fmtDate(lastRefreshAt)}</Fig></>}
              {sources.notice && <><Sep /><span className="fv--attend">{sources.notice}</span></>}
            </>
          : (sources.error ? t('Could not load the sources: {error}', { error: sources.error }) : t('Loading…'))}
        verbs={<button type="button" className="btn btn-accent" onClick={() => void refreshAll()}>{t('Refresh all')}</button>}
      />

      <Rail>
        <RailCell
          label={t('Sources')}
          value={sources.data ? enabledCount : t('not collected')}
          tone={!sources.data ? 'unknown' : 'ink'}
          detail={sources.data ? t('of {n}', { n: total }) : t('counting…')}
        />
        <RailCell
          label={t('Documents')}
          value={sources.data ? docsCurrent : t('not collected')}
          tone={!sources.data ? 'unknown' : docsCurrent === 0 ? 'nil' : 'ink'}
          detail={sources.data ? t('{n} superseded', { n: docsSuperseded }) : t('counting…')}
        />
        <RailCell
          label={t('Changed at last check')}
          value={sources.data ? changedAtLastCheck : t('not collected')}
          tone={!sources.data ? 'unknown' : changedAtLastCheck === 0 ? 'nil' : 'ok'}
          detail={sources.data ? t('by the latest refresh of each') : t('counting…')}
        />
        <RailCell
          label={t('Entries')}
          value={sources.data ? entriesCurrent : t('not collected')}
          tone={!sources.data ? 'unknown' : entriesCurrent === 0 ? 'nil' : 'ink'}
          detail={sources.data ? t('{resolved} on the record · {open} waiting', { resolved: entriesResolved, open: entriesWaiting }) : t('counting…')}
        />
        <RailCell
          label={t('Series points')}
          value={sources.data ? seriesPoints : t('not collected')}
          tone={!sources.data ? 'unknown' : seriesPoints === 0 ? 'nil' : 'ink'}
          detail={sources.data ? t('{n} series', { n: seriesCount }) : t('counting…')}
        />
      </Rail>

      <Zone id="sources">
        <ZoneHead label={t('Sources')} count={sources.data ? total : undefined} />
        <Sheet clip>
          {!sources.data ? (
            <p className="zone-empty">{sources.error ? t('Could not load the sources: {error}', { error: sources.error }) : t('Loading…')}</p>
          ) : rows.length === 0 ? (
            <p className="zone-empty">{t('No source registered yet.')}</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <Tbl>
                <thead>
                  <tr>
                    <th></th>
                    <th>{t('Source')}</th>
                    <th>{t('kind')}</th>
                    <th>{t('cadence')}</th>
                    <th>{t('detection')}</th>
                    <th>{t('status')}</th>
                    <th>{t('last checked')}</th>
                    <th>{t('last changed')}</th>
                    <th>{t('filed')}</th>
                    <th>{t('next check')}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    const isOpen = expandedKey === row.key;
                    const overridden = Boolean((row.settings as Record<string, unknown> | undefined)?.detection_overrides
                      ?? (row.settings as Record<string, unknown> | undefined)?.per_document_detection);
                    const status = !row.enabled
                      ? <span className="fv--nil voice-figure">{t('disabled')}</span>
                      : row.last_refresh_outcome === 'no_parser'
                        ? <span className="fv--unknown" title={row.last_refresh_error ?? undefined}>{t('parser not built yet')}</span>
                      : row.last_refresh_outcome === 'failed' || row.last_refresh_outcome === 'refused'
                        ? <span className="fv--attend" title={row.last_refresh_error ?? undefined}>{row.last_refresh_error ?? t(row.last_refresh_outcome)}</span>
                        : row.last_refresh_outcome
                          ? <Tag tone={outcomeTone(row.last_refresh_outcome)}>{t(row.last_refresh_outcome)}</Tag>
                          : <span className="fv--unknown">{t('not collected')}</span>;
                    return (
                      <Fragment key={row.key}>
                        <tr id={`source-${row.key}`} className={isOpen ? 'row--open' : undefined}>
                          <td>
                            <input
                              type="checkbox"
                              checked={row.enabled}
                              onChange={() => void toggleEnabled(row)}
                              title={row.enabled ? t('Disable this source') : t('Enable this source')}
                            />
                          </td>
                          <td>
                            <Lnk onClick={() => setExpandedKey(isOpen ? null : row.key)}>
                              <span className="voice-record">{row.name}</span>
                            </Lnk>
                            <div className="d"><a href={row.url} target="_blank" rel="noreferrer">{row.url}</a></div>
                          </td>
                          <td>{row.kind}</td>
                          <td>{row.cadence}</td>
                          <td>
                            {overridden
                              ? <Tag tone="ghost">{t('per document')}</Tag>
                              : row.detection.map((d) => <Tag key={d}>{DETECTION_LABEL[d]}</Tag>)}
                          </td>
                          <td>{status}</td>
                          <td>{row.last_checked_at ? <Fig>{fmtDateTime(row.last_checked_at)}</Fig> : <span className="fv--unknown">{t('not collected')}</span>}</td>
                          <td>{row.last_changed_at ? <Fig>{fmtDateTime(row.last_changed_at)}</Fig> : <span className="fv--unknown">{t('not collected')}</span>}</td>
                          <td>
                            <Fig>{row.entries_current ?? 0}</Fig> · <Fig>{row.series_points ?? 0}</Fig> ·{' '}
                            <Att kind="held" /> <Fig>{row.links_held ?? 0}</Fig>{' '}
                            <Lnk onClick={() => open(ROUTES.review({ principal: `source:${row.key}` }))} title={t('Open in Review')}>
                              <Att kind="proposed" /> <Fig>{row.links_waiting ?? 0}</Fig>
                            </Lnk>
                          </td>
                          <td>{t('daily · {signal}', { signal: DETECTION_LABEL[row.detection[0]] ?? '—' })}</td>
                          <td>
                            <button type="button" className="btn btn-sm" disabled={busyKey === row.key} onClick={() => void doRefreshOne(row.key)}>
                              {t('Refresh now')}
                            </button>
                          </td>
                        </tr>
                        {isOpen && (
                          <tr className="row--detail">
                            <td colSpan={11}>
                              <SourceRowDetail sourceKey={row.key} settings={row.settings} />
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </Tbl>
            </div>
          )}
        </Sheet>
      </Zone>

      <Zone id="documents">
        <ZoneHead label={t('Documents')} note={t('Every vintage kept; the newest is what the parsers read')} />
        <Sheet clip>
          {!documents.data ? (
            <p className="zone-empty">{documents.error ? t('Could not load the documents: {error}', { error: documents.error }) : t('Loading…')}</p>
          ) : documents.data.items.length === 0 ? (
            <p className="zone-empty">{t('No document fetched yet.')}</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <Tbl>
                <thead>
                  <tr>
                    <th>{t('source')}</th>
                    <th>{t('filename')}</th>
                    <th>{t('version')}</th>
                    <th>{t('Last-Modified')}</th>
                    <th>{t('sha256')}</th>
                    <th>{t('fetched')}</th>
                    <th>{t('parsed')}</th>
                  </tr>
                </thead>
                <tbody>
                  {documents.data.items.filter((d) => d.status === 'current').map((doc) => (
                    <DocumentRow
                      key={doc.id}
                      doc={doc}
                      open={openDocGroups.has(String(doc.id))}
                      onToggle={() => setOpenDocGroups((s) => {
                        const next = new Set(s);
                        if (next.has(String(doc.id))) next.delete(String(doc.id)); else next.add(String(doc.id));
                        return next;
                      })}
                    />
                  ))}
                </tbody>
              </Tbl>
            </div>
          )}
        </Sheet>
      </Zone>

      <Zone id="refreshes">
        <ZoneHead label={t('Refreshes')} count={refreshes.data ? refreshes.data.total : undefined} />
        <Sheet clip>
          {!refreshes.data ? (
            <p className="zone-empty">{refreshes.error ? t('Could not load the refreshes: {error}', { error: refreshes.error }) : t('Loading…')}</p>
          ) : refreshes.data.items.length === 0 ? (
            <p className="zone-empty">{t('No refresh has run yet.')}</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <Tbl>
                <thead>
                  <tr>
                    <th>{t('when')}</th>
                    <th>{t('source')}</th>
                    <th>{t('outcome')}</th>
                    <th className="num">{t('documents new')}</th>
                    <th className="num">{t('entries added/gone')}</th>
                    <th className="num">{t('points new/superseded')}</th>
                    <th className="num">{t('claims filed/held/waiting')}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {refreshes.data.items.map((r) => (
                    <RefreshRow
                      key={r.id}
                      r={r}
                      open={expandedRefresh === r.id}
                      onToggle={() => setExpandedRefresh((id) => (id === r.id ? null : r.id))}
                    />
                  ))}
                </tbody>
              </Tbl>
            </div>
          )}
        </Sheet>
      </Zone>

      <Zone id="waiting">
        <ZoneHead label={t('Waiting')} count={waiting.data ? waiting.data.total : undefined} />
        <Sheet clip>
          {!waiting.data ? (
            <p className="zone-empty">{waiting.error ? t('Could not load the waiting entries: {error}', { error: waiting.error }) : t('Loading…')}</p>
          ) : waiting.data.items.length === 0 ? (
            <p className="zone-empty">{t('Nothing waiting — every named entry is either resolved or on the record.')}</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <Tbl>
                <thead>
                  <tr>
                    <th>{t('name')}</th>
                    <th>{t('source')}</th>
                    <th>{t('category')}</th>
                    <th>{t('locality')}</th>
                    <th>{t('state')}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {waiting.data.items.map((entry) => {
                    // §3.3 (d): the register candidates of an `ambiguous` entry are kept under
                    // `raw._register` — uid, name, municipality as the mirror printed them.
                    const candidates = Array.isArray((entry.raw as Record<string, unknown> | undefined)?._register)
                      ? (entry.raw as { _register: Array<{ uid?: string; name?: string }> })._register
                      : null;
                    return (
                      <tr key={entry.id}>
                        <td><span className="voice-record">{entry.name}</span></td>
                        <td><Ref>{entry.source_key}</Ref></td>
                        <td>{entry.category ?? <span className="fv--unknown">{t('not collected')}</span>}</td>
                        <td>{entry.locality ?? <span className="fv--unknown">{t('not collected')}</span>}</td>
                        <td>
                          <Tag tone={entry.resolution === 'proposed' ? 'ghost' : 'attend'}>{t(entry.resolution)}</Tag>
                          {candidates && candidates.length > 0 && (
                            <div className="d">{candidates.map((c) => c.name ?? c.uid).filter(Boolean).join(' · ')}</div>
                          )}
                        </td>
                        <td>
                          {entry.resolution === 'proposed'
                            ? <Lnk onClick={() => open(ROUTES.review({ principal: `source:${entry.source_key}` }))}>{t('Open in Review')}</Lnk>
                            : <button type="button" className="btn btn-sm" onClick={() => void doPropose(entry)}>{t('Propose')}</button>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Tbl>
            </div>
          )}
        </Sheet>
      </Zone>

      <Zone id="gone">
        <ZoneHead
          label={t('gone since last vintage')}
          count={gone.data ? <Fig>{gone.data.total}</Fig> : undefined}
          note={t('The organisation, its facts and its links are untouched — a refresh retracts nothing.')}
        />
        <Sheet clip>
          {!gone.data ? (
            <p className="zone-empty">{gone.error ? t('Could not load the entries that left: {error}', { error: gone.error }) : t('Loading…')}</p>
          ) : gone.data.items.length === 0 ? (
            <p className="zone-empty">{t('Every name a source printed before, it prints still.')}</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <Tbl>
                <thead>
                  <tr>
                    <th>{t('name')}</th>
                    <th>{t('source')}</th>
                    <th>{t('category')}</th>
                    <th>{t('locality')}</th>
                    <th>{t('state')}</th>
                    <th>{t('gone')}</th>
                  </tr>
                </thead>
                <tbody>
                  {gone.data.items.map((entry) => (
                    <tr key={entry.id}>
                      <td>
                        {entry.organisation_id != null
                          ? <Lnk onClick={() => open(ROUTES.org({ id: entry.organisation_id! }))}><span className="voice-record">{entry.name}</span></Lnk>
                          : <span className="voice-record">{entry.name}</span>}
                      </td>
                      <td><Ref>{entry.source_key}</Ref></td>
                      <td>{entry.category ?? <span className="fv--unknown">{t('not collected')}</span>}</td>
                      <td>{entry.locality ?? <span className="fv--unknown">{t('not collected')}</span>}</td>
                      <td><Tag tone="attend">{t(entry.resolution)}</Tag></td>
                      <td>{entry.gone_at ? <Fig>{fmtDate(entry.gone_at)}</Fig> : <span className="fv--unknown">{t('not collected')}</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </Tbl>
            </div>
          )}
        </Sheet>
      </Zone>
    </PageShell>
  );
}

// ── a source row's detail: its documents, its last report, its settings ────────────────────
function SourceRowDetail({ sourceKey, settings }: { sourceKey: string; settings: Record<string, unknown> }) {
  const t = useT();
  const { fmtDateTime } = useLocale();
  const fetchDocs = useCallback(() => listDocuments(sourceKey), [sourceKey]);
  const fetchLast = useCallback(() => listRefreshes(sourceKey, 1), [sourceKey]);
  const docs = usePoll(fetchDocs, 60_000);
  const last = usePoll(fetchLast, 60_000);
  const lastReport = last.data?.items[0];

  return (
    <div className="src-detail" style={{ display: 'flex', gap: 24, flexWrap: 'wrap', padding: '12px 0' }}>
      <div style={{ minWidth: 260, flex: '1 1 260px' }}>
        <div className="u-label">{t('Documents')}</div>
        {!docs.data ? <p className="zone-empty">{t('Loading…')}</p> : docs.data.items.length === 0 ? (
          <p className="zone-empty">{t('No document fetched yet.')}</p>
        ) : (
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {docs.data.items.map((d) => (
              <li key={d.id}>
                <a href={`/api/snapshots/${d.snapshot_id ?? ''}/file`} target="_blank" rel="noreferrer">{d.filename ?? d.url}</a>
                {' '}{d.status === 'superseded' && <Tag tone="ghost">{t('superseded')}</Tag>}
                {' '}<Fig>{fmtDateTime(d.fetched_at)}</Fig>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div style={{ minWidth: 220, flex: '1 1 220px' }}>
        <div className="u-label">{t('last report')}</div>
        {!lastReport ? <p className="zone-empty">{t('No refresh has run yet.')}</p> : (
          <dl className="src-dl">
            <dt>{t('outcome')}</dt><dd><Tag tone={outcomeTone(lastReport.outcome)}>{lastReport.outcome ? t(lastReport.outcome) : '—'}</Tag></dd>
            <dt>{t('documents new')}</dt><dd><Fig>{lastReport.report && 'documents' in lastReport.report ? lastReport.report.documents.new.length : 0}</Fig></dd>
            <dt>{t('when')}</dt><dd><Fig>{fmtDateTime(lastReport.started_at)}</Fig></dd>
          </dl>
        )}
      </div>
      <div style={{ minWidth: 220, flex: '1 1 220px' }}>
        <div className="u-label">{t('settings')}</div>
        <pre style={{ margin: 0, fontSize: 12, whiteSpace: 'pre-wrap' }}>{JSON.stringify(settings ?? {}, null, 2)}</pre>
      </div>
    </div>
  );
}

function DocumentRow({ doc, open: isOpen, onToggle }: { doc: SourceDocument; open: boolean; onToggle: () => void }) {
  const t = useT();
  const { fmtDate, fmtDateTime } = useLocale();
  const fileHref = doc.snapshot_id ? `/api/snapshots/${doc.snapshot_id}/file` : undefined;
  return (
    <>
      <tr>
        <td><Ref>{doc.source_key}</Ref></td>
        <td>
          {fileHref ? <a href={fileHref} target="_blank" rel="noreferrer">{doc.filename ?? doc.url}</a> : (doc.filename ?? doc.url)}
          {doc.superseded_by != null && (
            <Lnk onClick={onToggle} title={t('show the previous vintage')}>{' '}({isOpen ? t('hide') : t('show')} {t('superseded')})</Lnk>
          )}
        </td>
        <td>{doc.version_token ?? <span className="fv--unknown">{t('not collected')}</span>}</td>
        <td>{doc.last_modified ? <Fig>{fmtDate(doc.last_modified)}</Fig> : <span className="fv--unknown">{t('not collected')}</span>}</td>
        <td><Ref title={doc.sha256}>{doc.sha256.slice(0, 12)}</Ref></td>
        <td><Fig>{fmtDateTime(doc.fetched_at)}</Fig></td>
        <td>
          {doc.parsed_at
            ? <span className="voice-figure">{doc.parse_note ?? '—'}</span>
            : <span className="fv--unknown">{t('kept · no series named')}</span>}
        </td>
      </tr>
      {isOpen && (
        <tr className="row--detail">
          <td colSpan={7} className="d">{t('Every vintage kept; the newest is what the parsers read')}</td>
        </tr>
      )}
    </>
  );
}

function RefreshRow({ r, open: isOpen, onToggle }: { r: SourceRefresh; open: boolean; onToggle: () => void }) {
  const t = useT();
  const { fmtDateTime } = useLocale();
  const rep = r.report && 'documents' in r.report ? r.report : null;
  return (
    <>
      <tr>
        <td><Fig>{fmtDateTime(r.started_at)}</Fig></td>
        <td><Ref>{r.source_key}</Ref></td>
        <td><Tag tone={outcomeTone(r.outcome)}>{r.outcome ? t(r.outcome) : t('running')}</Tag></td>
        <td className="num"><Fig>{rep?.documents.new.length ?? 0}</Fig></td>
        <td className="num"><Fig>{rep?.entries.added.length ?? 0}</Fig> / <Fig>{rep?.entries.gone.length ?? 0}</Fig></td>
        <td className="num"><Fig>{rep?.series.new_points ?? 0}</Fig> / <Fig>{rep?.series.superseded_points.length ?? 0}</Fig></td>
        <td className="num"><Fig>{rep?.claims.filed.length ?? 0}</Fig> / <Fig>{rep?.claims.held_on_arrival.length ?? 0}</Fig> / <Fig>{rep?.claims.waiting_in_review.length ?? 0}</Fig></td>
        <td><Lnk onClick={onToggle}>{isOpen ? t('hide') : t('report')}</Lnk></td>
      </tr>
      {isOpen && (
        <tr className="row--detail">
          <td colSpan={8}>
            {r.error && <p className="fv--broken">{r.error}</p>}
            {rep ? (
              <dl className="src-dl">
                <dt>{t('entries resolved')}</dt>
                <dd>{rep.entries.resolved.length === 0 ? '—' : rep.entries.resolved.map((e) => <span key={e.entry_key} style={{ marginRight: 8 }}><RefLnk>{e.organisation_ref}</RefLnk></span>)}</dd>
                <dt>{t('entries proposed')}</dt>
                <dd>{rep.entries.proposed.length === 0 ? '—' : rep.entries.proposed.map((e) => <span key={e.entry_key} style={{ marginRight: 8 }}><RefLnk>{e.proposal_ref}</RefLnk></span>)}</dd>
                <dt>{t('claims held on arrival')}</dt>
                <dd>{rep.claims.held_on_arrival.length === 0 ? '—' : rep.claims.held_on_arrival.map((ref) => <span key={ref} style={{ marginRight: 8 }}><RefLnk>{ref}</RefLnk></span>)}</dd>
                <dt>{t('claims waiting in Review')}</dt>
                <dd>{rep.claims.waiting_in_review.length === 0 ? '—' : rep.claims.waiting_in_review.map((ref) => <span key={ref} style={{ marginRight: 8 }}><RefLnk>{ref}</RefLnk></span>)}</dd>
                <dt>{t('anomalies')}</dt>
                <dd>{rep.anomalies.length === 0 ? '—' : rep.anomalies.map((a, i) => <div key={i}>{a.where}: {a.issue}</div>)}</dd>
              </dl>
            ) : <p className="zone-empty">{t('This refresh has not finished yet.')}</p>}
          </td>
        </tr>
      )}
    </>
  );
}
